<template>
  <div class="current-cost-dialog-container">
    <div class="data-containers">
      <div class="data-container">
        <h3>bank</h3>
        <div class="data-container-rows">
          <div class="data-container-row">
            <span>{{ selectedBankName }}</span>
          </div>
        </div>
      </div>

      <div class="data-container bank-fees-data-container">
        <div class="bank-fees-title">
          <h3>bankszámla díjak</h3>

          <button type="button" class="bank-fees-info-button" @click.stop="toggleBankFeesInfo" @blur="hideBankFeesInfo"
            aria-label="Információ a kalkulációról" :aria-expanded="isBankFeesInfoOpen ? 'true' : 'false'">
            <img :src="infoLogo" alt="Info" height="24px" />
          </button>

          <span class="bank-fees-info-tooltip" :class="{ 'is-open': isBankFeesInfoOpen }">
            A kalkuláció nem tartalmaz olyan díjtételeket amik nem a lakossági forint folyószámlához kapcsolódnak, így
            nem
            számol az értékpapírszámla, megtakarítási számla- vagy devizaszámla számlavezetési díjaival, letéti számla
            számlavezetési díjaival, személyi kölcsönhöz és jelzálog hitelhez kapcsolódó díjakkal, fizetendő kamatokkal,
            fióki trezorbérlés illetve olyan szolgáltatások áraival, amelyek nem részei a mindennapi számlahasználatnak.
          </span>
        </div>

        <div class="data-container-rows bank-fees-rows">
          <div class="data-container-double-row">
            <div class="data-container-row">
              <h4>kártyadíj</h4>
              <span>{{ balanceFormatter(accountInfo.cardFee) }}</span>
            </div>

            <div class="data-container-row">
              <h4>számladíj</h4>
              <span>{{ balanceFormatter(accountInfo.accountFee) }}</span>
            </div>
          </div>

          <div class="data-container-double-row">
            <div class="data-container-row">
              <h4>átutalások díja</h4>
              <span>{{ balanceFormatter(accountInfo.transferFees) }}</span>
            </div>

            <div class="data-container-row">
              <h4>állandó átutalások díja</h4>
              <span>{{ balanceFormatter(accountInfo.recurringTransferFee) }}</span>
            </div>
          </div>

          <div class="data-container-double-row">
            <div class="data-container-row">
              <h4>csoportos beszedés díja</h4>
              <span>{{ balanceFormatter(accountInfo.groupDirectDebitFee) }}</span>
            </div>

            <div class="data-container-row">
              <h4>készpénzfelvétel díja</h4>
              <span>{{ balanceFormatter(accountInfo.cashWithdrawalFee) }}</span>
            </div>
          </div>

          <div class="data-container-row">
            <h4>egyéb díjak</h4>
            <span>{{ balanceFormatter(accountInfo.otherFees) }}</span>
          </div>
        </div>
      </div>

      <div class="data-container transaction-data-container">
        <h3>bankszámla díjak részletesen</h3>

        <div class="transaction-list" v-if="feeAndDiscountTransactions.length">
          <div class="transaction-scroll-container">
            <div v-for="transaction in paginatedTransactions" :key="transaction.UUID || transaction.McTransactionId"
              class="transaction-card">
              <div class="transaction-summary-row">
                <div class="transaction-summary-cell transaction-recipient-cell"
                  v-if="transaction.Recipient && transaction.Recipient.Name">
                  <h4>kedvezményezett</h4>
                  <span class="text">{{ transaction.Recipient.Name }}</span>
                </div>

                <div class="transaction-summary-cell transaction-amount-cell">
                  <h4>összeg</h4>
                  <span class="text">
                    {{ amountPrefix(transaction.CreditDebitIndicator) }}
                    {{ balanceFormatter(transaction.Amount, transaction.Currency) }}
                  </span>
                </div>

                <div class="transaction-summary-cell transaction-date-cell" v-if="transaction.BookingDate">
                  <h4>könyvelés dátuma</h4>
                  <span class="text">{{ formatBookingDate(transaction.BookingDate) }}</span>
                </div>

                <div class="transaction-summary-cell transaction-category-cell">
                  <h4>kategória</h4>
                  <span class="text">{{ categoryLabel(transaction.category) }}</span>
                </div>

                <div class="transaction-summary-cell transaction-information-cell"
                  v-if="transaction.AdditionalInformation">
                  <h4>közlemény / további információ</h4>
                  <span class="other-information">{{ transaction.AdditionalInformation }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="transaction-pagination" v-if="totalPages > 1">
            <button type="button" @click="previousPage" :disabled="currentPage === 1">
              előző
            </button>

            <span>{{ currentPage }} / {{ totalPages }}</span>

            <button type="button" @click="nextPage" :disabled="currentPage === totalPages">
              következő
            </button>
          </div>
        </div>

        <div class="empty-transaction-list" v-else>
          Nincs megjeleníthető banki díjtétel.
        </div>
      </div>
    </div>

    <div class="recalculate-button-container" v-if="showButton">
      <div class="recalculate-button" @click="$emit('recalculate')">
        módosítom a K&amp;H kalkulációt
      </div>
    </div>
  </div>
</template>

<script>
import "./_vpd-current-cost.scss";
import infoLogo from "@/assets/info-circle.svg";

const costCategoryLabels = {
  ACCOUNT_FEE: "számladíj",
  CARD_FEE: "kártyadíj",
  TRANSFER_FEE: "átutalások díja",
  RECURRING_TRANSFER_FEE: "állandó átutalások díja",
  GROUP_DIRECT_DEBIT_FEE: "csoportos beszedés díja",
  CASH_WITHDRAWAL_FEE: "készpénzfelvétel díja",
  OTHER_FEE: "egyéb díjak",
  ACCOUNT_DISCOUNT: "díjkedvezmény",
};

export default {
  name: "vpd-current-cost",
  props: {
    accountInfo: {
      type: Object,
      default: () => ({})
    },
    selectedBankName: {
      type: String
    },
    showButton: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      currentPage: 1,
      pageSize: 5,
      infoLogo,
      isBankFeesInfoOpen: false
    };
  },
  computed: {
    feeAndDiscountTransactions() {
      return this.accountInfo.feeAndDiscountTransactions || [];
    },
    paginatedTransactions() {
      const startIndex = (this.currentPage - 1) * this.pageSize;
      return this.feeAndDiscountTransactions.slice(startIndex, startIndex + this.pageSize);
    },
    totalPages() {
      return Math.ceil(this.feeAndDiscountTransactions.length / this.pageSize);
    },
    balanceFormatter() {
      return (amount, currency = "HUF") =>
        new Intl.NumberFormat("hu-HU", {
          style: "currency",
          currency,
          maximumFractionDigits: 0
        }).format(Number(amount || 0));
    }
  },
  watch: {
    feeAndDiscountTransactions() {
      this.currentPage = 1;
    }
  },
  methods: {
    amountPrefix(creditDebitIndicator) {
      return creditDebitIndicator === "CREDIT" ? "+" : "-";
    },
    formatBookingDate(bookingDate) {
      if (!bookingDate) {
        return "";
      }

      return bookingDate.split(" ")[0];
    },
    previousPage() {
      if (this.currentPage > 1) {
        this.currentPage -= 1;
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage += 1;
      }
    },
    toggleBankFeesInfo() {
      this.isBankFeesInfoOpen = !this.isBankFeesInfoOpen;
    },
    hideBankFeesInfo() {
      this.isBankFeesInfoOpen = false;
    },
    categoryLabel(category) {
      if (!category) {
        return "-";
      }

      return costCategoryLabels[category] || category;
    },
  }
};
</script>
